import { config } from '../../utils/config';
import { identityAuth } from '../../auth/IdentityAuth';
import { handle } from './identityHttp';

// EDU-Intelligence credits & reporting API (FastAPI, separate host). All routes here
// are bearer-guarded with the same super-admin Identity token. `period` is `YYYY-MM`
// (Europe/London); omit to default to the current month. Responses are resolved through the
// shared `handle` (it keeps the backend message and populates `.status`, which the credit
// modals rely on).

const baseUrl = () => {
  const url = config.EI_API_URL;
  if (!url) {
    throw new Error(
      'EI API URL is not configured. Set REACT_APP_EI_API_URL in your environment.'
    );
  }
  return url.replace(/\/$/, '');
};

const authHeaders = async () => {
  const token = await identityAuth.getValidToken();
  if (!token) {
    throw new Error('You must be signed in to view AI usage.');
  }
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

// Array values expand into a repeated key (e.g. `month=2026-06&month=2026-07`); scalar values
// are appended once. Empty/absent values are dropped. Ids and values are URL-encoded here.
const qs = (params) => {
  const search = new URLSearchParams();
  const append = (key, value) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, value);
  };
  Object.entries(params || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => append(key, item));
      return;
    }
    append(key, value);
  });
  const s = search.toString();
  return s ? `?${s}` : '';
};

// async so a synchronous config/token failure surfaces as a rejected promise.
const get = async (path, params) =>
  handle(await fetch(`${baseUrl()}${path}${qs(params)}`, { headers: await authHeaders() }));

const send = async (method, path, jsonBody) =>
  handle(
    await fetch(`${baseUrl()}${path}`, {
      method,
      headers: await authHeaders(),
      body: jsonBody === undefined ? undefined : JSON.stringify(jsonBody),
    })
  );

// --- Section B: platform-wide (home dashboard) -----------------------------
export const getPlatformSummary = (period) =>
  get('/api/admin/credits/summary', { period });
export const getPlatformBalances = (period) =>
  get('/api/admin/credits/balances', { period }); // heavy per-school fan-out; drill-in only
// Lightweight per-org usage rollup (id, type, resolved name, group_total, consumed, pct) —
// one query, no per-school fan-out, so it returns in <10s. Server-side ranked by `sortBy`
// then paginated (`limit`/`offset`), so a page is a true top-N. Powers the Platform Usage list
// and lists standalone schools (individual datasets) as org-level rows.
export const getOrgsSummary = ({ period, sortBy = 'usage', order, limit = 25, offset = 0 } = {}) =>
  get('/api/admin/credits/orgs/summary', { period, sort_by: sortBy, order, limit, offset });
export const getOrgs = () => get('/api/admin/credits/orgs');
// `sortBy` selects the server-side ordering metric (`messages` | `threads` | `cost` |
// `credits`); an unknown value is rejected by the API with a 422.
export const getLeaderboard = ({ scope = 'school', period, limit = 20, sortBy = 'messages' } = {}) =>
  get('/api/reporting/leaderboard', { scope, period, limit, sort_by: sortBy });

// --- Section A: account-view balances --------------------------------------
export const getGroupBalance = (groupId, period) =>
  get(`/api/credits/groups/${encodeURIComponent(groupId)}/balance`, { period });
export const getSchoolBalance = (schoolId, period) =>
  get(`/api/admin/credits/schools/${encodeURIComponent(schoolId)}/balance`, { period });
export const getSchoolProportions = (schoolId, period) =>
  get(`/api/credits/schools/${encodeURIComponent(schoolId)}/proportions`, { period });
export const getOrgBalance = (orgId, period) =>
  get(`/api/admin/credits/orgs/${encodeURIComponent(orgId)}/balance`, { period });

// --- Section A: admin credit actions ---------------------------------------
// Grant additional credits to a user for the month.
export const grantUserCredits = ({ period, schoolId, userId, credits, reason }) =>
  send('POST', '/api/admin/credits/grants', {
    period,
    school_id: schoolId,
    user_id: userId,
    scope: 'user',
    credits,
    reason,
  });
// Increase a school's total for the month, distributed equally (omit user_id).
export const increaseSchoolCredits = ({ period, schoolId, credits, reason }) =>
  send('POST', '/api/admin/credits/grants', {
    period,
    school_id: schoolId,
    scope: 'school',
    credits,
    reason,
  });
// Increase a group's total for the month (equal split across the group's schools).
export const increaseGroupCredits = ({ period, groupId, credits, reason }) =>
  send('POST', '/api/admin/credits/grants', {
    period,
    group_id: groupId,
    scope: 'group',
    credits,
    reason,
  });
// Change a user's monthly credits — this month only (absolute, resets next month).
export const setUserMonthAllowance = ({ period, schoolId, userId, credits }) =>
  send('PUT', '/api/admin/credits/user-allowance/month', {
    period,
    school_id: schoolId,
    user_id: userId,
    credits,
  });
// Change a user's monthly credits — permanently (standing override).
export const setUserAllowance = ({ schoolId, userId, credits }) =>
  send('PUT', '/api/admin/credits/user-allowance', {
    school_id: schoolId,
    user_id: userId,
    credits,
  });

// --- Section C: conversation & usage analytics -----------------------------
export const getReportingOverview = ({ scope, id, period }) =>
  get('/api/reporting/overview', { scope, id, period });
export const getThreads = ({ scope, id, period, include, limit, offset, search }) =>
  get('/api/reporting/threads', { scope, id, period, include, limit, offset, search });
export const getThread = (threadId, { period, include } = {}) =>
  get(`/api/reporting/threads/${encodeURIComponent(threadId)}`, { period, include });
export const getFeedback = ({ scope, id, period, limit }) =>
  get('/api/reporting/feedback', { scope, id, period, limit });
// Per-interaction history — feeds the per-user drill-down.
export const getUserHistory = (userId, { schoolId, period } = {}) =>
  get(`/api/admin/credits/users/${encodeURIComponent(userId)}/history`, {
    school_id: schoolId,
    period,
  });
// Daily usage series for a user/school/group, feeding the Daily Credit Usage chart. `months`
// is an array of YYYY-MM (sent as a repeatable `month` param, max 12); omit for the current
// month. Days with no activity are omitted by the API and gap-filled client-side.
export const getDailyUsage = ({ scope, id, months } = {}) =>
  get('/api/reporting/usage/daily', { scope, id, month: months });
